import type { CartItem } from '../store/usePosStore';
import type { CartEvaluationResult, PromotionWithTargets } from '../../promotions/domain/promotionTypes';
import { summarizeCheckoutDiscounts } from '../../promotions/application/posCartPromotionPresentation';
import {
  findPromotionByCode,
  listCashierSelectablePromotions,
  validatePromoCodeForCart,
} from './posCartPromotions';

export const STALE_PROMO_MESSAGE = 'Esta promo ya no vale' as const;
export const STALE_PROMO_RESTORE_TOAST = 'La promo que tenías ya no vale' as const;

export type CartPromoReconcilePatch = {
  clearAppliedPromoCode: boolean;
  clearSelectedCashierPromotionId: boolean;
  promoCodeError: string | null;
};

export type CartPromoUiStatus = {
  effectiveLabel: string | null;
  staleManualCode: string | null;
  staleCashierPromoName: string | null;
  hasStaleSelection: boolean;
  staleMessage: string | null;
  reconcilePatch: CartPromoReconcilePatch | null;
};

export function buildEffectivePromoLabel(params: {
  evaluation: CartEvaluationResult | null;
  appliedPromoCode: string | null;
  selectedCashierPromotionId: string | null;
  promotions: PromotionWithTargets[];
}): string | null {
  const { evaluation, appliedPromoCode, selectedCashierPromotionId, promotions } = params;
  if (!evaluation || evaluation.discountTotal <= 0) return null;

  const rows = summarizeCheckoutDiscounts(evaluation);
  if (rows.length === 0) return null;

  const manualPromo = appliedPromoCode ? findPromotionByCode(appliedPromoCode, promotions) : null;
  if (manualPromo && rows.some((row) => row.promotionId === manualPromo.id)) {
    return `Código ${appliedPromoCode}`;
  }

  const cashierPromo = selectedCashierPromotionId
    ? promotions.find((promo) => promo.id === selectedCashierPromotionId)
    : null;
  if (cashierPromo && rows.some((row) => row.promotionId === cashierPromo.id)) {
    return cashierPromo.name;
  }

  const names = [...new Set(rows.map((row) => row.promotionName))];
  if (names.length === 1) return names[0] ?? null;
  return names.join(' · ');
}

export function resolveCartPromoUiStatus(params: {
  cart: CartItem[];
  branchId: string | null | undefined;
  promotions: PromotionWithTargets[];
  appliedPromoCode: string | null;
  selectedCashierPromotionId: string | null;
  evaluation: CartEvaluationResult | null;
  now?: Date;
}): CartPromoUiStatus {
  const {
    cart,
    branchId,
    promotions,
    appliedPromoCode,
    selectedCashierPromotionId,
    evaluation,
    now = new Date(),
  } = params;

  const effectiveLabel = buildEffectivePromoLabel({
    evaluation,
    appliedPromoCode,
    selectedCashierPromotionId,
    promotions,
  });

  let staleManualCode: string | null = null;
  if (appliedPromoCode?.trim() && branchId && cart.length > 0) {
    const validation = validatePromoCodeForCart(appliedPromoCode, cart, promotions, branchId, now);
    if (!validation.ok) {
      staleManualCode = appliedPromoCode;
    }
  } else if (appliedPromoCode?.trim()) {
    staleManualCode = appliedPromoCode;
  }

  let staleCashierPromoName: string | null = null;
  if (selectedCashierPromotionId && branchId && cart.length > 0) {
    const eligible = listCashierSelectablePromotions(cart, promotions, branchId, now);
    const stillEligible = eligible.some((promo) => promo.id === selectedCashierPromotionId);
    if (!stillEligible) {
      const known = promotions.find((promo) => promo.id === selectedCashierPromotionId);
      staleCashierPromoName = known?.name ?? 'Promoción elegida';
    }
  } else if (selectedCashierPromotionId) {
    const known = promotions.find((promo) => promo.id === selectedCashierPromotionId);
    staleCashierPromoName = known?.name ?? 'Promoción elegida';
  }

  const hasStaleSelection = staleManualCode != null || staleCashierPromoName != null;

  return {
    effectiveLabel,
    staleManualCode,
    staleCashierPromoName,
    hasStaleSelection,
    staleMessage: hasStaleSelection ? STALE_PROMO_MESSAGE : null,
    reconcilePatch: hasStaleSelection
      ? {
          clearAppliedPromoCode: staleManualCode != null,
          clearSelectedCashierPromotionId: staleCashierPromoName != null,
          promoCodeError: staleManualCode != null ? STALE_PROMO_MESSAGE : null,
        }
      : null,
  };
}
